import clsx from "clsx"

import { dashIfZero } from "@/lib/format"
import { aggregateWorkload, sumWorkload, type WorkloadRow } from "@/lib/workload"
import type { WorkItem } from "@/types/sprint-report"

interface Props {
  items: WorkItem[]
  /** Current-sprint bundles each person leads (Standup owner), keyed by name. */
  bundlesLed?: Record<string, number>
}

/** A small "i" badge with a styled tooltip shown on hover / keyboard focus. */
function InfoTip({ label, text }: { label: string; text: string }) {
  return (
    <span className="group/tip relative inline-flex">
      <span
        tabIndex={0}
        role="img"
        aria-label={label}
        className="inline-flex h-3.5 w-3.5 cursor-help items-center justify-center rounded-full bg-white/20 text-[9px] font-bold text-white ring-1 ring-white/50 transition hover:bg-white/30"
      >
        i
      </span>
      <span
        role="tooltip"
        className="pointer-events-none absolute left-1/2 top-full z-30 mt-2 hidden w-60 -translate-x-1/2 rounded-md bg-ink px-3 py-2 text-left text-[11px] font-normal normal-case leading-snug tracking-normal text-white shadow-lg group-hover/tip:block group-focus-within/tip:block"
      >
        {text}
      </span>
    </span>
  )
}

export function DeveloperWorkload({ items, bundlesLed }: Props) {
  const rows = aggregateWorkload(items, bundlesLed)

  if (rows.length === 0) {
    return (
      <p className="rounded border border-slate-300 p-6 text-center text-sm text-slate-500">
        No items assigned in the current sprint.
      </p>
    )
  }

  const totals = sumWorkload(rows)

  return (
    <div className="overflow-x-auto rounded border border-slate-300">
      <table className="matrix-table">
        <thead>
          <tr>
            <th rowSpan={2} className="sticky left-0 z-10 bg-header text-left">
              Assignee
            </th>
            <th rowSpan={2} className="bg-header">
              <span className="inline-flex items-start justify-center gap-1">
                <span className="leading-tight">
                  Bundles
                  <br />
                  led
                </span>
                <InfoTip
                  label="About the Bundles led column"
                  text="Current-sprint bundles this person leads — i.e. they're the Standup 'owner' (the bundle's Project Item assignee). The bug/PBI work inside a bundle may be assigned to other people, so this is separate from their directly-assigned counts."
                />
              </span>
            </th>
            <th colSpan={4} className="bg-headerSub">By priority</th>
            <th colSpan={4} className="bg-headerSub">By stage (current sprint)</th>
            <th rowSpan={2}>Remaining<br/>(not Done)</th>
          </tr>
          <tr>
            <th className="bg-headerSub">P0</th>
            <th className="bg-headerSub">P1</th>
            <th className="bg-headerSub">P2</th>
            <th className="bg-headerSub">P3</th>
            <th className="bg-headerSub">To Do</th>
            <th className="bg-headerSub">In Dev</th>
            <th className="bg-headerSub">In QA</th>
            <th className="bg-headerSub">Done</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <WorkloadRowCells key={row.assignee} row={row} />
          ))}
          <tr className="subtotal">
            <td className="row-label sticky left-0 z-10 bg-slate-100">All assignees</td>
            <td className="font-semibold">{dashIfZero(totals.bundlesLed)}</td>
            <WorkloadCounts row={totals} />
          </tr>
        </tbody>
      </table>
    </div>
  )
}

function WorkloadRowCells({ row }: { row: WorkloadRow }) {
  return (
    <tr>
      <td
        className={clsx(
          "row-label sticky left-0 z-10 bg-white",
          row.isUnassigned && "italic text-slate-500",
        )}
      >
        {row.assignee}
      </td>
      <td className={clsx(row.bundlesLed > 0 && "font-semibold text-ink")}>
        {dashIfZero(row.bundlesLed)}
      </td>
      <WorkloadCounts row={row} />
    </tr>
  )
}

/** The numeric cells shared by data rows and the totals row. */
function WorkloadCounts({ row }: { row: WorkloadRow }) {
  return (
    <>
      <td className={clsx(row.p0 > 0 && "font-bold text-priority-p0")}>{dashIfZero(row.p0)}</td>
      <td>{dashIfZero(row.p1)}</td>
      <td>{dashIfZero(row.p2)}</td>
      <td>{dashIfZero(row.p3)}</td>
      <td>{dashIfZero(row.toDo)}</td>
      <td>{dashIfZero(row.inDev)}</td>
      <td>{dashIfZero(row.inQa)}</td>
      <td className="done-cell">{dashIfZero(row.done)}</td>
      <td className="remaining-cell font-semibold">{dashIfZero(row.remaining)}</td>
    </>
  )
}
